import {
  AfterContentInit,
  afterNextRender,
  AfterViewInit,
  Component,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  viewChild,
  ViewEncapsulation
} from '@angular/core'
import {
  FullCalendarComponent,
  FullCalendarModule
} from '@fullcalendar/angular'
import { CalendarOptions, EventInput } from '@fullcalendar/core/index.js'
import { TranslatePipe } from '@ngx-translate/core'
import { Subscription } from 'rxjs'
import { LanguageService, Locale } from '../../../services/language.service'
import daygridPlugin from '@fullcalendar/daygrid'
import cnLocale from '@fullcalendar/core/locales/zh-cn'
import twLocale from '@fullcalendar/core/locales/zh-tw'
import { EventImpl } from '@fullcalendar/core/internal'

export type DayInfo = {
  date: Date
  events: EventImpl[] | []
}

export type DatesSetInfo = {
  start: string
  end: string
}

@Component({
  selector: 'schedule-calendar',
  templateUrl: './schedule-calendar.component.html',
  styleUrls: ['./schedule-calendar.component.css', './full-calendar.style.css'],
  imports: [TranslatePipe, FullCalendarModule],
  encapsulation: ViewEncapsulation.None
})
export class ScheduleCalendar implements OnDestroy {
  private localeMap = {
    [Locale.EN]: 'en',
    [Locale.ZH_CN]: cnLocale.code,
    [Locale.ZH_HK]: twLocale.code
  }

  events = input<EventInput[]>([])
  deepChangeDetection = input<boolean>()
  dayClickEvent = output<DayInfo>({ alias: 'dayClick' })
  todayInitEvent = output<DayInfo>({ alias: 'todayInit' })
  datesSetEvent = output<DatesSetInfo>({ alias: 'datesSet' })
  calendar = viewChild.required<FullCalendarComponent>('calendar')
  langService = inject(LanguageService)
  langChangeSubs: Subscription | undefined
  calendarOptions: CalendarOptions = {
    initialView: 'dayGridMonth',
    plugins: [daygridPlugin],
    locales: [cnLocale, twLocale],
    headerToolbar: { start: 'prev', center: 'title', end: 'next' },
    height: 'auto',
    contentHeight: 'auto',
    viewClassNames: ['schedule-custom-mark'],
    dayHeaderClassNames: ['bg-secondary-purple-10', 'py-1', 'fs-5'],
    dayCellClassNames: ['fs-5', 'pt-1'],
    dayHeaderContent: this.dayHeaderContent.bind(this),
    dayCellContent: this.dayCellContent.bind(this),
    eventContent: this.eventContent.bind(this),
    datesSet: this.datesSet.bind(this),
    viewDidMount: this.dayInit.bind(this)
  }

  constructor() {
    this.langChangeSubs = this.langService.langSource.subscribe((locale) => {
      this.calendarOptions.locale = this.localeMap[locale]
    })
  }

  dayHeaderContent(info: any) {
    let text = info.text
    let locale = info.view.getOption('locale')

    if (!locale) {
      return text
    }

    if (locale === Locale.EN) {
      return text.at(0)
    }

    return text.replace('周', '').replace('週', '')
  }

  dayCellContent(info: any) {
    return Number.parseInt(info.dayNumberText)
  }

  eventContent() {
    return { html: `<span class="fas fa-circle text-accent-orange"></span>` }
  }

  datesSet(info: any) {
    let elDay = this.calendar().getApi().el.querySelectorAll('.fc-daygrid-day')

    elDay.forEach((el) => {
      if (el.hasAttribute('day-click-label')) {
        return
      }
      el.setAttribute('day-click-label', 'true')
      el.addEventListener('click', () =>
        this.dayClick(el.getAttribute('data-date'))
      )
    })

    this.datesSetEvent.emit({
      start: info.startStr.split('T')[0],
      end: info.endStr.split('T')[0]
    })
  }

  dayClick(dateStr: any) {
    if (!dateStr) {
      return
    }
    this.dayClickEvent.emit(this.getDayInfo(dateStr))
  }

  dayInit() {
    let api = this.calendar().getApi()
    let dateStr = api.getDate().toISOString().split('T')[0]
    this.todayInitEvent.emit(this.getDayInfo(dateStr))
  }

  getDayInfo(date: string): DayInfo {
    let api = this.calendar().getApi()
    let _date = new Date(date)
    let events = api
      .getEvents()
      .filter((ev) => ev.start?.getTime() === _date.getTime())

    return { date: _date, events }
  }

  ngOnDestroy(): void {
    this.langChangeSubs?.unsubscribe()
  }
}
